import React, { useState, useEffect, useMemo } from 'react';
import { X, Layers, Info, Settings, Box, Package, Check, AlertCircle, HelpCircle } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import SheetsGrid from './SheetsGrid';
import { ColorPalette, DensityPicker } from './MaterialDensities';

const TABS = [
  { id: 'identificacao', label: 'Identificação', icon: Info },
  { id: 'propriedades', label: 'Propriedades', icon: Settings },
  { id: 'chapas', label: 'Chapas', icon: Box },
  { id: 'retalhos', label: 'Retalhos', icon: Package }
];

const MATERIAL_TYPES = [
  'MDF', 'MDP', 'COMPENSADO', 'OSB',
  'MACICO', 'VIDRO', 'METAL', 'PEDRA',
  'ACM', 'ACRILICO', 'TECIDO', 'ALUMINIO',
  'OUTROS'
];

const initialFormState = {
  description: '',
  materialType: 'MDF',
  reference: '',
  manufacturer: '',
  finish: '',
  collection: '',
  color: '',
  colorHex: '#ffffff',
  imageUrl: '',
  isActive: true,
  isFavorite: false,
  cuttingProfileId: null,
  cuttingProfile: 'PADRAO',
  grainAllowed: true,
  thickness: 18,
  materialDensityId: null,
  density: '',
  generateScrap: false,
  minScrapLength: '',
  minScrapWidth: '',
  useMinArea: false,
  minArea: '',
  minStockLimit: 0,
  primaryEdgeBand: '',
  secondaryEdgeBand: '',
  tertiaryEdgeBand: '',
  sheets: [],
  scraps: []
};

const BoardPatternFormTabs = ({ pattern = null, onClose, onSuccess }) => {
  const isEditing = !!pattern?.id;

  const [activeTab, setActiveTab] = useState('identificacao');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [profiles, setProfiles] = useState([]);

  useEffect(() => {
    loadProfiles();
    if (isEditing) {
      loadPattern();
    }
  }, []);

  const loadProfiles = async () => {
    try {
      const response = await api.get('/cutting-profiles');
      if (response.data.success) {
        setProfiles(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar perfis:', error);
    }
  };

  const loadPattern = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/board-patterns/${pattern.id}`);
      if (response.data.success) {
        const p = response.data.data;
        setFormData({
          description: p.description || '',
          materialType: p.material_type || 'MDF',
          reference: p.reference || '',
          manufacturer: p.manufacturer || '',
          finish: p.finish || '',
          collection: p.collection || '',
          color: p.color || '',
          colorHex: p.color_hex || '#ffffff',
          imageUrl: p.image_url || '',
          isActive: p.is_active ?? true,
          isFavorite: p.is_favorite ?? false,
          cuttingProfileId: p.cutting_profile_id || null,
          cuttingProfile: p.cutting_profile || 'PADRAO',
          grainAllowed: p.grain_allowed ?? true,
          thickness: p.thickness || 18,
          materialDensityId: p.material_density_id || null,
          density: p.density_value || p.density || '',
          generateScrap: p.generate_scrap ?? false,
          minScrapLength: p.min_scrap_length || '',
          minScrapWidth: p.min_scrap_width || '',
          useMinArea: p.use_min_area ?? false,
          minArea: p.min_area || '',
          minStockLimit: p.min_stock_limit || 0,
          primaryEdgeBand: p.primary_edge_band || '',
          secondaryEdgeBand: p.secondary_edge_band || '',
          tertiaryEdgeBand: p.tertiary_edge_band || '',
          sheets: (p.sheets || []).map(s => ({
            ...s,
            id: s.id,
            tempId: undefined
          })),
          scraps: (p.scraps || []).map(s => ({
            ...s,
            id: s.id,
            tempId: undefined
          }))
        });
      }
    } catch (error) {
      console.error('Erro ao carregar padrão:', error);
      toast.error('Erro ao carregar padrão');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSheetsChange = (newSheets) => {
    setFormData(prev => ({ ...prev, sheets: newSheets }));
  };

  const handleScrapsChange = (newScraps) => {
    setFormData(prev => ({ ...prev, scraps: newScraps }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.description?.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    } else if (formData.description.length > 90) {
      newErrors.description = 'Descrição deve ter no máximo 90 caracteres';
    }
    if (formData.reference && formData.reference.length > 20) {
      newErrors.reference = 'Referência deve ter no máximo 20 caracteres';
    }
    if (!formData.thickness || parseFloat(formData.thickness) <= 0) {
      newErrors.thickness = 'Espessura deve ser maior que zero';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) {
      toast.error('Corrija os erros antes de salvar');
      setActiveTab('identificacao');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        thickness: parseFloat(formData.thickness) || 18,
        density: formData.density ? parseFloat(formData.density) : null,
        minScrapLength: formData.minScrapLength ? parseFloat(formData.minScrapLength) : null,
        minScrapWidth: formData.minScrapWidth ? parseFloat(formData.minScrapWidth) : null,
        minArea: formData.minArea ? parseFloat(formData.minArea) : null,
        minStockLimit: parseInt(formData.minStockLimit) || 0,
        sheets: formData.sheets.map(s => ({
          ...s,
          length: parseFloat(s.length) || 0,
          width: parseFloat(s.width) || 0,
          quantity: parseInt(s.quantity) || 1,
          price: s.price ? parseFloat(s.price) : null
        })).filter(s => s.length > 0 && s.width > 0),
        scraps: formData.scraps.map(s => ({
          ...s,
          length: parseFloat(s.length) || 0,
          width: parseFloat(s.width) || 0,
          quantity: parseInt(s.quantity) || 1
        })).filter(s => s.length > 0 && s.width > 0)
      };

      const response = isEditing
        ? await api.put(`/board-patterns/${pattern.id}`, payload)
        : await api.post('/board-patterns', payload);

      if (response.data.success) {
        toast.success(isEditing ? 'Padrão atualizado com sucesso!' : 'Padrão criado com sucesso!');
        onSuccess?.(response.data.data);
        onClose();
      } else {
        toast.error(response.data.message || 'Erro ao salvar padrão');
      }
    } catch (error) {
      console.error('Erro ao salvar padrão:', error);
      const message = error.response?.data?.message || 'Erro ao salvar padrão';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const sheetsCount = useMemo(() => formData.sheets.filter(s => s.isEnabled !== false).length, [formData.sheets]);
  const scrapsCount = useMemo(() => formData.scraps.filter(s => s.isEnabled !== false).length, [formData.scraps]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
        <div className="bg-white rounded-xl shadow-2xl p-8">
          <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl overflow-hidden flex flex-col max-h-[95vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-gray-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Layers className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {isEditing ? 'Editar padrão de chapa' : 'Adicionar padrão de chapa'}
              </h2>
              <p className="text-xs text-gray-500">
                {isEditing ? 'Atualize as informações do padrão' : 'Cadastre um novo material para corte'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-white flex-shrink-0">
          <nav className="flex" aria-label="Tabs">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const badge = tab.id === 'chapas' ? sheetsCount : tab.id === 'retalhos' ? scrapsCount : null;

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    relative flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors
                    ${isActive
                      ? 'border-blue-600 text-blue-700 bg-blue-50/50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {badge !== null && badge > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                      isActive ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-700'
                    }`}>
                      {badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6">
            {activeTab === 'identificacao' && (
              <IdentificationTab
                formData={formData}
                onChange={handleFieldChange}
                errors={errors}
              />
            )}
            {activeTab === 'propriedades' && (
              <PropertiesTab
                formData={formData}
                onChange={handleFieldChange}
                profiles={profiles}
              />
            )}
            {activeTab === 'chapas' && (
              <SheetsTab
                sheets={formData.sheets}
                onChange={handleSheetsChange}
              />
            )}
            {activeTab === 'retalhos' && (
              <ScrapsTab
                scraps={formData.scraps}
                onChange={handleScrapsChange}
              />
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <div className="text-xs text-gray-500">
            {errors.description && (
              <span className="text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.description}
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {isEditing ? 'Atualizar Padrão' : 'Salvar Padrão'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// ABA 1: IDENTIFICAÇÃO
// ==========================================
const IdentificationTab = ({ formData, onChange, errors }) => {
  return (
    <div className="space-y-5">
      <SectionHeader icon={Info} title="Identificação do padrão" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Field label="Descrição do padrão de chapa *" helpText="Até 90 caracteres. Ex.: MDF Branco 18mm">
          <input
            type="text"
            maxLength={90}
            value={formData.description}
            onChange={(e) => onChange('description', e.target.value)}
            className={`w-full px-3 py-2.5 border rounded-lg text-sm ${
              errors.description ? 'border-red-500' : 'border-gray-300'
            } focus:ring-2 focus:ring-blue-500`}
            placeholder="Ex: MDF Branco 18mm Polido"
            autoFocus
          />
          {errors.description && <p className="text-xs text-red-600 mt-1">{errors.description}</p>}
        </Field>

        <Field label="Tipo de material" helpText="Selecione o tipo de material deste padrão">
          <select
            value={formData.materialType}
            onChange={(e) => onChange('materialType', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
          >
            {MATERIAL_TYPES.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Field>

        <Field label="Referência" helpText="Até 20 caracteres. Ex.: MDF-18-00023">
          <input
            type="text"
            maxLength={20}
            value={formData.reference}
            onChange={(e) => onChange('reference', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="Opcional"
          />
        </Field>

        <Field label="Fabricante" helpText="Até 100 caracteres">
          <input
            type="text"
            maxLength={100}
            value={formData.manufacturer}
            onChange={(e) => onChange('manufacturer', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="Ex: Duratex, Arauco, etc."
          />
        </Field>

        <Field label="Acabamento" helpText="Até 100 caracteres">
          <input
            type="text"
            maxLength={100}
            value={formData.finish}
            onChange={(e) => onChange('finish', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="Ex: Polido, Texturizado, Fosco"
          />
        </Field>

        <Field label="Coleção" helpText="Até 100 caracteres">
          <input
            type="text"
            maxLength={100}
            value={formData.collection}
            onChange={(e) => onChange('collection', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="Ex: Linha Premium, Standard"
          />
        </Field>
      </div>

      <SectionHeader icon={Layers} title="Visualização gráfica" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Field label="Cor" helpText="Selecione uma cor ou use a paleta">
          <ColorPalette
            value={formData.colorHex}
            onChange={(v) => onChange('colorHex', v)}
          />
        </Field>

        <Field label="Nome da cor" helpText="Nome descritivo da cor">
          <input
            type="text"
            value={formData.color}
            onChange={(e) => onChange('color', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="Ex: Branco Ártico, Carvalho Escuro"
          />
        </Field>

        <Field label="URL da imagem" helpText="Link para imagem representativa (opcional)">
          <input
            type="text"
            value={formData.imageUrl}
            onChange={(e) => onChange('imageUrl', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="https://..."
          />
        </Field>
      </div>

      {/* Preview */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-xs font-medium text-gray-500 mb-3 text-center">Visualização do material</p>
        <div className="flex items-center justify-center gap-4">
          <div
            className="w-20 h-20 rounded-lg border-2 flex items-center justify-center"
            style={{
              backgroundColor: formData.colorHex + '30',
              borderColor: formData.colorHex
            }}
          >
            <span className="text-sm font-bold text-gray-700">{formData.thickness}mm</span>
          </div>
          <div className="text-left">
            <p className="text-sm font-bold text-gray-900">{formData.description || 'Nome do padrão'}</p>
            <p className="text-xs text-gray-500">{formData.materialType} • {formData.thickness}mm</p>
            {formData.color && <p className="text-xs text-gray-500">{formData.color}</p>}
            {formData.reference && <p className="text-xs text-gray-400">Ref: {formData.reference}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// ABA 2: PROPRIEDADES
// ==========================================
const PropertiesTab = ({ formData, onChange, profiles }) => {
  return (
    <div className="space-y-5">
      <SectionHeader icon={Settings} title="Status e configuração" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Field label="Status" helpText="Padrões desativados param de aparecer nos menus de escolha">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onChange('isActive', true)}
              className={`flex-1 px-3 py-2.5 border rounded-lg text-sm font-medium ${
                formData.isActive
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              ✓ Ativo
            </button>
            <button
              type="button"
              onClick={() => onChange('isActive', false)}
              className={`flex-1 px-3 py-2.5 border rounded-lg text-sm font-medium ${
                !formData.isActive
                  ? 'border-gray-500 bg-gray-100 text-gray-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              ✕ Inativo
            </button>
          </div>
        </Field>

        <Field label="Favoritar" helpText="Chapas favoritas aparecem no início da lista de seleção">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onChange('isFavorite', true)}
              className={`flex-1 px-3 py-2.5 border rounded-lg text-sm font-medium ${
                formData.isFavorite
                  ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              ⭐ Sim
            </button>
            <button
              type="button"
              onClick={() => onChange('isFavorite', false)}
              className={`flex-1 px-3 py-2.5 border rounded-lg text-sm font-medium ${
                !formData.isFavorite
                  ? 'border-gray-500 bg-gray-100 text-gray-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Não
            </button>
          </div>
        </Field>

        <Field label="Perfil de cálculo" helpText="Configuração de kerf, refile e rotação do padrão">
          <select
            value={formData.cuttingProfileId || ''}
            onChange={(e) => onChange('cuttingProfileId', e.target.value ? parseInt(e.target.value) : null)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Selecione...</option>
            {profiles.map(p => (
              <option key={p.id} value={p.id}>
                {p.name} {p.is_global ? '(Global)' : ''}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Veio (textura, canela ou grão)" helpText="Define se permite rotação das peças no corte">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onChange('grainAllowed', false)}
              className={`flex-1 px-3 py-2 border rounded-lg text-sm font-medium ${
                !formData.grainAllowed
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Sim (não permite rotação)
            </button>
            <button
              type="button"
              onClick={() => onChange('grainAllowed', true)}
              className={`flex-1 px-3 py-2 border rounded-lg text-sm font-medium ${
                formData.grainAllowed
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Não (permite rotação)
            </button>
          </div>
        </Field>
      </div>

      <SectionHeader icon={Box} title="Dimensões e peso" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Field label="Espessura *" helpText="Em milímetros (mm)">
          <div className="flex items-center gap-2">
            <input
              type="number"
              step="0.1"
              min="0"
              value={formData.thickness}
              onChange={(e) => onChange('thickness', parseFloat(e.target.value) || 0)}
              className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-500">mm</span>
          </div>
        </Field>

        <Field label="Densidade (peso por m³)" helpText="Em kg/m³ - usado para cálculo de peso das peças">
          <div className="flex items-center gap-2">
            <input
              type="number"
              step="0.0001"
              min="0"
              value={formData.density}
              onChange={(e) => onChange('density', e.target.value)}
              className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: 700"
            />
            <span className="text-sm text-gray-500">kg/m³</span>
          </div>
        </Field>

        <Field label="Material (catálogo)" helpText="Selecione do catálogo para preencher a densidade">
          <DensityPicker
            value={formData.materialDensityId}
            onChange={(id) => onChange('materialDensityId', id)}
            densityValue={formData.density}
            onDensityValueChange={(v) => onChange('density', v)}
          />
        </Field>

        <Field label="Limite mínimo de chapas em estoque" helpText="Para alertas de controle de estoque">
          <input
            type="number"
            min="0"
            value={formData.minStockLimit}
            onChange={(e) => onChange('minStockLimit', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          />
        </Field>
      </div>

      <SectionHeader icon={Package} title="Geração de retalhos" />

      <div className="space-y-3">
        <Field label="Gerar retalho?" helpText="Define se o sistema identifica retalhos aproveitáveis no cálculo">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onChange('generateScrap', true)}
              className={`flex-1 px-3 py-2.5 border rounded-lg text-sm font-medium ${
                formData.generateScrap
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Sim
            </button>
            <button
              type="button"
              onClick={() => onChange('generateScrap', false)}
              className={`flex-1 px-3 py-2.5 border rounded-lg text-sm font-medium ${
                !formData.generateScrap
                  ? 'border-gray-500 bg-gray-100 text-gray-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Não
            </button>
          </div>
        </Field>

        {formData.generateScrap && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-4 bg-blue-50/50 rounded-lg border border-blue-100">
            <Field label="Comprimento mínimo (mm)">
              <input
                type="number"
                min="0"
                value={formData.minScrapLength}
                onChange={(e) => onChange('minScrapLength', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="mm"
              />
            </Field>

            <Field label="Largura mínima (mm)">
              <input
                type="number"
                min="0"
                value={formData.minScrapWidth}
                onChange={(e) => onChange('minScrapWidth', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="mm"
              />
            </Field>

            <Field label="Usar área mínima">
              <div className="flex items-center gap-2 h-full">
                <input
                  type="checkbox"
                  id="useMinArea"
                  checked={formData.useMinArea}
                  onChange={(e) => onChange('useMinArea', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="useMinArea" className="text-sm text-gray-700">
                  Considerar área mínima ao invés de dimensões
                </label>
              </div>
            </Field>

            {formData.useMinArea && (
              <Field label="Área mínima (m²)">
                <input
                  type="number"
                  step="0.001"
                  min="0"
                  value={formData.minArea}
                  onChange={(e) => onChange('minArea', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Ex: 0.5"
                />
              </Field>
            )}
          </div>
        )}
      </div>

      <SectionHeader icon={Layers} title="Fitas de borda" />
      <p className="text-xs text-gray-500 -mt-3">
        Fitas classificadas como primária/secundária/terciária aparecem no início da lista no cadastro de peças
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Field label="Fita primária">
          <input
            type="text"
            maxLength={100}
            value={formData.primaryEdgeBand}
            onChange={(e) => onChange('primaryEdgeBand', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="Ex: Borda 1mm Preta"
          />
        </Field>

        <Field label="Fita secundária">
          <input
            type="text"
            maxLength={100}
            value={formData.secondaryEdgeBand}
            onChange={(e) => onChange('secondaryEdgeBand', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="Ex: Borda 2mm Branca"
          />
        </Field>

        <Field label="Fita terciária">
          <input
            type="text"
            maxLength={100}
            value={formData.tertiaryEdgeBand}
            onChange={(e) => onChange('tertiaryEdgeBand', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="Ex: Borda 0.4mm Mescla"
          />
        </Field>
      </div>
    </div>
  );
};

// ==========================================
// ABA 3: CHAPAS
// ==========================================
const SheetsTab = ({ sheets, onChange }) => {
  return (
    <div className="space-y-4">
      <SectionHeader icon={Box} title="Chapas disponíveis" />
      <p className="text-xs text-gray-500 -mt-3">
        Cadastre as chapas físicas disponíveis neste padrão. Apenas chapas com "Hab" marcado serão usadas no cálculo.
      </p>

      <SheetsGrid
        items={sheets}
        onChange={onChange}
        showPrice={true}
        showLocation={true}
        showObservation={true}
        showRegisterDate={true}
        enableToggle={true}
        emptyMessage="Nenhuma chapa cadastrada. Clique em 'Adicionar linha' para começar."
      />
    </div>
  );
};

// ==========================================
// ABA 4: RETALHOS
// ==========================================
const ScrapsTab = ({ scraps, onChange }) => {
  return (
    <div className="space-y-4">
      <SectionHeader icon={Package} title="Retalhos disponíveis em estoque" />
      <p className="text-xs text-gray-500 -mt-3">
        Retalhos são sobras aproveitáveis de cortes anteriores. Apenas retalhos com "Hab" marcado serão usados como matéria-prima.
      </p>

      <SheetsGrid
        items={scraps}
        onChange={onChange}
        showPrice={false}
        showLocation={true}
        showObservation={true}
        showRegisterDate={true}
        enableToggle={true}
        emptyMessage="Nenhum retalho cadastrado. Clique em 'Adicionar linha' para começar."
      />
    </div>
  );
};

// ==========================================
// COMPONENTES AUXILIARES
// ==========================================
const SectionHeader = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
    <div className="bg-blue-100 p-1.5 rounded">
      <Icon className="w-3.5 h-3.5 text-blue-700" />
    </div>
    <h3 className="text-xs font-bold uppercase tracking-wider text-gray-600">{title}</h3>
  </div>
);

const Field = ({ label, children, helpText }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1.5">
      {label}
    </label>
    {children}
    {helpText && (
      <p className="text-xs text-gray-500 mt-1 flex items-start gap-1">
        <HelpCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>{helpText}</span>
      </p>
    )}
  </div>
);

export default BoardPatternFormTabs;